// Sprint 9 — HR & Payroll types

// Employment category — matches backend VALID_WORKER_CATEGORIES.
// NOTE: SKILLED/SEMI_SKILLED/UNSKILLED/TRAINEE used to live here, but
// those are skill *levels* and now live on Employee.worker_skill_class
// (Phase HR-SKILL). Keeping TRAINEE + CONSULTANT for legacy data —
// safe to drop once those rows are migrated to the new fields.
export const EMPLOYEE_CATEGORIES = [
  'PERMANENT', 'CONTRACT', 'STAFF', 'APPRENTICE', 'CONSULTANT', 'TRAINEE',
] as const
export type EmployeeCategory = (typeof EMPLOYEE_CATEGORIES)[number]

// Shift rotation policy — different from the actual shift the worker is
// assigned to (that's Employee.default_shift_id, a FK to the shifts master).
// shift_type is POLICY: ROTATING (rotates A→B→C every week) vs FIXED_x.
export const SHIFT_TYPE_POLICIES = [
  'ROTATING', 'GENERAL', 'FIXED_A', 'FIXED_B', 'FIXED_C',
] as const
export type ShiftTypePolicy = (typeof SHIFT_TYPE_POLICIES)[number]

export const NOMINEE_RELATIONSHIPS = [
  'SPOUSE', 'FATHER', 'MOTHER', 'SON', 'DAUGHTER', 'BROTHER', 'SISTER',
  'FATHER_IN_LAW', 'MOTHER_IN_LAW', 'GRANDFATHER', 'GRANDMOTHER', 'OTHER',
] as const

// Minimum Wages Act wage-board categories. Distinct from worker_skill_class
// (which is operational) and category (employment type). This drives the
// minimum wage rate the worker is entitled to under labour law.
export const WAGE_CATEGORIES = [
  'UNSKILLED', 'SEMI_SKILLED', 'SKILLED', 'HIGHLY_SKILLED',
] as const

// Common PPE / uniform sizes — standardised so HR can scan a Size column
// without seeing 'L' / 'Large' / 'large' / 'l' all coexist for the same size.
export const PPE_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'] as const
export const UNIFORM_SIZES = PPE_SIZES
// Indian shoe sizes (UK system, which is what's used in safety shoes here).
export const SHOE_SIZES = ['5', '6', '7', '8', '9', '10', '11', '12', '13'] as const

// Top Indian banks (covers ~90% of working-class accounts). Free text still
// supported via Select's showSearch + creatable pattern for the long tail
// of co-operative banks / small finance banks.
export const INDIAN_BANKS = [
  'State Bank of India',
  'Bank of Baroda',
  'Punjab National Bank',
  'Canara Bank',
  'Union Bank of India',
  'Bank of India',
  'Indian Bank',
  'Central Bank of India',
  'UCO Bank',
  'Bank of Maharashtra',
  'Indian Overseas Bank',
  'Punjab and Sind Bank',
  // Private
  'HDFC Bank',
  'ICICI Bank',
  'Axis Bank',
  'Kotak Mahindra Bank',
  'IndusInd Bank',
  'Yes Bank',
  'IDFC FIRST Bank',
  'Federal Bank',
  'South Indian Bank',
  'Karnataka Bank',
  'Karur Vysya Bank',
  'City Union Bank',
  // Small finance / niche
  'Equitas Small Finance Bank',
  'AU Small Finance Bank',
  'Ujjivan Small Finance Bank',
  // Payments / postal
  'India Post Payments Bank',
] as const

// Indian states + UTs. Used for current_state / permanent_state pickers
// in AddressTab. Sorted alphabetically for fast scan.
export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal',
  // Union Territories
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi',
  'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
] as const
export type ShiftType = 'ROTATING' | 'GENERAL' | 'FIXED_A' | 'FIXED_B' | 'FIXED_C'
export type SalaryType = 'MONTHLY' | 'DAILY' | 'PIECE_RATE'
export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'HALF_DAY' | 'ON_LEAVE' | 'COMP_OFF' | 'WEEKLY_OFF'
export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED'
export type RosterStatus = 'PLANNED' | 'CONFIRMED' | 'CHANGED'
export type PayrollStatus = 'OPEN' | 'PROCESSING' | 'COMPUTED' | 'APPROVED' | 'PAID'

export interface Employee {
  id: string
  emp_code: string
  emp_name: string
  department_id: string
  department_name: string
  plant_id: string
  plant_name: string
  designation?: string
  joining_date: string
  category: EmployeeCategory
  shift_type: ShiftType
  qualification?: string
  phone?: string
  email?: string
  salary_type: SalaryType
  basic_salary: string
  pf_no?: string
  esi_no?: string
  user_id?: string
  is_active: boolean
  // Phase HR-PHOTO-001 + HR-002 — workforce-study fields. All optional;
  // legacy rows return null.
  photo_url?: string | null
  village?: string | null
  pincode?: string | null
  reporting_supervisor_id?: string | null
  reporting_supervisor_name?: string | null
  // Contractor Foundation — link a contract worker to their contractor
  contractor_id?: string | null
  // Phase HR-003 Wave 1 — workforce-study master fields. All optional.
  gender?: string | null
  date_of_birth?: string | null
  marital_status?: string | null
  transport_mode?: string | null
  transport_route_code?: string | null
  wage_payout_group?: string | null
  language_preference?: string | null
  exit_date?: string | null
  exit_reason_code?: string | null
  exit_interview_done?: boolean
  tenure_band_at_exit?: string | null
  rehire_eligible?: boolean | null
  created_at: string
}

export interface LeaveType {
  id: string
  leave_code: string
  leave_name: string
  days_per_year: number
  carry_forward: boolean
  max_accumulation: number
  is_active: boolean
  created_at: string
}

export interface LeaveBalance {
  id: string
  employee_id: string
  employee_name: string
  leave_type_id: string
  leave_type_name: string
  year: number
  opening: string
  availed: string
  closing: string
}

export interface LeaveApplication {
  id: string
  employee_id: string
  employee_name: string
  leave_type_id: string
  leave_type_name: string
  from_date: string
  to_date: string
  days: string
  reason?: string
  status: LeaveStatus
  approved_by?: string
  approved_at?: string
  remarks?: string
  created_at: string
}

export interface ShiftRoster {
  id: string
  employee_id: string
  employee_name: string
  shift_id: string
  shift_name: string
  roster_date: string
  roster_status: RosterStatus
}

// Phase HR-003 Wave 2 — Absenteeism Study reason codes. Doc §11.1
// root-cause walk uses these as the single-pick enum on the roster card.
export type AbsentReasonCode =
  | 'UNINFORMED'
  | 'SICK_LEAVE'
  | 'FESTIVAL'
  | 'AGRICULTURAL'
  | 'FAMILY'
  | 'TRANSPORT'
  | 'WAGE_RELATED'
  | 'OTHER'

export interface Attendance {
  id: string
  attendance_date: string
  employee_id: string
  employee_name: string
  department_id: string
  department_name: string
  shift_id: string
  shift_name: string
  status: AttendanceStatus
  check_in_time?: string
  check_out_time?: string
  overtime_hours: string
  remarks?: string
  // Phase HR-003 Wave 2 — Absenteeism Study fields. All optional;
  // legacy rows + non-ABSENT statuses leave them null.
  absent_reason_code?: AbsentReasonCode | null
  absent_reason_text?: string | null
  call_verified_at?: string | null
  call_verified_by?: string | null
  replacement_employee_id?: string | null
  is_repeat_absentee?: boolean
  repeat_count_month?: number
  created_at: string
}

export interface AttendanceSummary {
  date: string
  department_id: string
  shift_id: string
  present: number
  absent: number
  half_day: number
  on_leave: number
  total: number
}

export interface PayrollPeriod {
  id: string
  plant_id: string
  plant_name: string
  year: number
  month: number
  status: PayrollStatus
  opened_at: string
  computed_at?: string
  approved_by?: string
  approved_at?: string
  created_at: string
}

export interface PaySlip {
  id: string
  employee_id: string
  employee_name: string
  emp_code: string
  period_id: string
  days_present: string
  days_absent: string
  overtime_hours: string
  basic_earned: string
  hra: string
  da: string
  overtime_amount: string
  gross: string
  pf_deduction: string
  esi_deduction: string
  tds: string
  other_deductions: string
  net_pay: string
  created_at: string
}
